import { Box, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import ResultCTAOptions from './ResultCTAOptions';
import React, { useEffect, useRef, useState } from 'react';
import WhatYourResultsMean from '../components/pages/howTo/howToUnderstandPFAS/WhatYourResultsMean';
import HowToReducePfas from '../components/pages/howTo/howToUnderstandPFAS/HowToReducePFAS';
import AboutTheTestMethodology from '../components/pages/howTo/howToUnderstandPFAS/AboutTheTestMethodology';
import { useSearchParams } from 'react-router-dom';
import { Unstable_Grid2 as Grid } from '@mui/material';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import { useLatestReport } from '../utils/reports';
import GradientCircularProgress from '../components/common/GradientCircularProgress';

type TabContent = {
  title: string;
};

const HowToUnderstandPfasCorePanelResult = () => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // When user lands to this from the Result Card's `Total PFAS` click, we need to show the "Total PFAS" section within the `WhatYourResultsMean`
  const totalPfasRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  const getValidTabIndex = (paramValue: string | null) => {
    const parsed = parseInt(paramValue ?? '0');
    const maxTabs = 3;
    return isNaN(parsed) || parsed < 0 || parsed >= maxTabs ? 0 : parsed;
  };

  const [activeTab, setActiveTab] = useState(() => {
    return getValidTabIndex(searchParams.get('activeTab'));
  });

  const [reportData, setReportData] = useState<any>();
  const { latestReportLoading, fetchLatestReport } = useLatestReport();

  useEffect(() => {
    const fetchLatestReportsFunction = async () => {
      let latestReportResponse = await fetchLatestReport();
      if (!latestReportResponse.message && latestReportResponse.latestReport) {
        setReportData(latestReportResponse.latestReport);
      }
    };
    fetchLatestReportsFunction();
  }, [fetchLatestReport]);
  const isReportReady = !!reportData && !latestReportLoading;

  useEffect(() => {
    if (searchParams.get('activeTab') && totalPfasRef.current) {
      totalPfasRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [searchParams]);

  // Only for the second tab(in the middle)
  // to scroll and center it for certain screen sizes
  useEffect(() => {
    if (activeTab === 1) {
      const currentActiveTabElement = document.querySelector(`[role='tab']:nth-child(${activeTab + 1})`);
      setTimeout(() => {
        currentActiveTabElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 500);
    }
  }, [activeTab]);

  const renderHeaderDetails = () => {
    return (
      <Grid container spacing={3}>
        {/* Left Image Section */}
        <Grid
          sx={{
            order: { xs: 2, smMd700: 1 },
            width: { xs: '100%', smMd700: '45%' },
          }}
        >
          <Box
            component="img"
            src="/images/how-to-understand-pfas-page-header-image.png"
            alt="PFAS Sample"
            sx={{
              objectFit: 'cover',
              width: { xs: '100%', md: '100%' },
              height: { xs: 180, smMd700: '100%', md: '235px' },
              borderRadius: '16px',
              flexShrink: 0,
            }}
          />
        </Grid>

        {/* Right Text Section */}
        <Grid
          sx={{
            order: { xs: 1, smMd700: 2 },
            width: { xs: '100%', smMd700: '55%' },
          }}
        >
          <Typography
            sx={{
              color: theme.custom.report.howToUnderstandPfasCorePanelResult.typography.color,
              textAlign: 'left',
              fontSize: { xs: '40px', sm: '40px', smMd700: '46px', md: '47px' },
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: { xs: '43px', smMd700: '49px', md: '52px' },
              letterSpacing: '-0.25px',
              marginBottom: '0',
            }}
          >
            How to understand your PFAS Core Panel result
          </Typography>

          <Typography
            variant="h4"
            sx={{
              marginTop: '16px',
              textAlign: 'left',
              color: theme.custom.report.howToUnderstandPfasCorePanelResult.typography.h4.color,
              fontWeight: 400,
              fontSize: { xs: '18px', md: '22px' },
              fontStyle: 'normal',
              lineHeight: '28px',
            }}
          >
            This guide explains the results of your PFAS Core Panel. It is available as a download.
          </Typography>
        </Grid>
      </Grid>
    );
  };

  // The following box is for the explainer
  const renderExplainer = () => {
    return (
      <Box
        sx={{
          mt: '10px',
          display: 'flex',
          alignItems: { xs: 'center', md: 'flex-start' },
          alignSelf: 'stretch',
          textAlign: 'left',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          marginBottom: { xs: '12px', md: '8px' },
        }}
      >
        <Typography
          component="span"
          sx={{
            color: theme.custom.report.howToUnderstandPfasCorePanelResult.renderExplainer.color,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
          }}
        >
          Please note that this test is intended to provide clinical information regarding the presence or absence of
          the specified PFAS compounds. Results should be interpreted in conjunction with other clinical and laboratory
          findings. The results of this test are not intended to be the sole basis for diagnosis or clinical
          decision-making. This test was developed and its performance characteristics determined by Relentless Health.
          It has not been cleared or approved by the US Food and Drug Administration.
        </Typography>
      </Box>
    );
  };

  const renderResultCTAButtons = () => {
    return (
      <>
        {!isReportReady ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <GradientCircularProgress loadingMessage="Loading ..." />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              marginBottom: '32px',
            }}
          >
            <ResultCTAOptions showLatestResultButton testId={reportData?.test_id} />
          </Box>
        )}
      </>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabsContent: TabContent[] = [
    {
      title: 'What your results mean',
    },
    {
      title: 'How to reduce PFAS',
    },
    {
      title: 'About the test methodology',
    },
  ];

  const renderTabsTitle = () => {
    return (
      <Box
        sx={{
          width: '100%',
          borderBottom: 'none',
          borderColor: 'transparent',
          borderTopRightRadius: '16px',
          borderTopLeftRadius: '16px',
          padding: { sm: '24px 40px 0px 40px' },
          backgroundColor: theme.custom.report.howToUnderstandPfasCorePanelResult.renderTabsTitle.backgroundColor,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="How to collect a good sample"
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            width: '100%',
            '& .MuiButtonBase-root.MuiTab-root': {
              '@media (min-width: 379px) and (max-width: 400px)': {
                padding: '12px 12px !important',
              },
              '@media (max-width: 378px)': {
                padding: '12px 9px !important',
              },
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : '33.3%',
              color: theme.custom.report.howToUnderstandPfasCorePanelResult.renderTabsTitle.root.color,
            },
            '& .Mui-selected': {
              color: `${theme.custom.report.howToUnderstandPfasCorePanelResult.renderTabsTitle.selected.color} !important`,
            },
          }}
        >
          {tabsContent.map((tab, index) => (
            <Tab
              key={index}
              label={tab.title}
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.8rem', smMd700: '0.875rem' },
              }}
            />
          ))}
        </Tabs>
      </Box>
    );
  };

  const renderActiveTabTextInstructions = () => {
    return (
      <>
        <Box>{activeTab === 0 && <WhatYourResultsMean totalPfasRef={totalPfasRef} />}</Box>
        <Box>{activeTab === 1 && <HowToReducePfas />}</Box>
        <Box>{activeTab === 2 && <AboutTheTestMethodology />}</Box>
      </>
    );
  };

  const renderContentAndTabInstructions = () => {
    return <Box>{renderActiveTabTextInstructions()}</Box>;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          padding: { xs: '20px 16px', md: '40px 0px 24px 0px' },
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
          [theme.breakpoints.down('md')]: {
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
          },
        }}
      >
        {renderHeaderDetails()}
        {renderExplainer()}
        {renderResultCTAButtons()}
      </Box>
      <Box
        sx={{
          padding: { md: 0 },
        }}
      >
        {renderTabsTitle()}
        {renderContentAndTabInstructions()}
      </Box>
    </>
  );
};

export default HowToUnderstandPfasCorePanelResult;
